import CommonText from "@/components/text/CommonText";
import React, { FC, HTMLAttributes, useRef, useEffect, useCallback } from "react";

interface CustomDivProps extends HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  setCloseModal: (value: boolean) => void;
  headingText?: string | React.ReactNode;
  notShowCrossBtn?: boolean;
  showApplyButton?: boolean;
  applyButtonText?: string;
}

const ModalLayoutBottomFixed: FC<CustomDivProps> = ({
  children,
  setCloseModal,
  headingText,
  notShowCrossBtn,
  showApplyButton = false,
  applyButtonText = "Apply",
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);

  const handleCloseModal = useCallback(() => {
    // Force cleanup of any remaining modal states
    const cleanupModalStates = () => {
      // Remove any remaining modal classes
      document.body.classList.remove('modal-open');
      
      // Reset body styles
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.body.style.left = '';
      
      // Force a reflow to ensure styles are applied
      void document.body.offsetHeight;
    };
    
    // Call cleanup immediately
    cleanupModalStates();
    
    // Also call it after a short delay to ensure it takes effect
    setTimeout(cleanupModalStates, 100);
    
    setCloseModal(false);
  }, [setCloseModal]);

  const handleBackdropClick = useCallback((event: React.MouseEvent | React.TouchEvent) => {
    event.preventDefault();
    event.stopPropagation();
    handleCloseModal();
  }, [handleCloseModal]);

  const handleCloseButtonClick = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    handleCloseModal();
  }, [handleCloseModal]);

  // Prevent body scrolling when modal is open and handle keyboard events
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    const originalPosition = window.getComputedStyle(document.body).position;
    const originalTop = window.getComputedStyle(document.body).top;
    
    // Store original scroll position
    const scrollY = window.scrollY;
    
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.classList.add('modal-open');
    
    // Handle Escape key to close modal
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCloseModal();
      }
    };
    
    document.addEventListener('keydown', handleEscapeKey);
    
    // Handle window resize to ensure proper cleanup
    const handleWindowResize = () => {
      if (!document.body.classList.contains('modal-open')) {
        // If modal is not open but body still has fixed positioning, reset it
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.height = '';
        document.body.style.left = '';
      }
    };
    
    window.addEventListener('resize', handleWindowResize);
    
    return () => {
      // Restore body scrolling and position when modal is closed
      document.body.style.overflow = originalStyle;
      document.body.style.position = originalPosition;
      document.body.style.top = originalTop;
      document.body.style.width = '';
      document.body.classList.remove('modal-open');
      
      // Restore scroll position
      window.scrollTo(0, scrollY);
      
      document.removeEventListener('keydown', handleEscapeKey);
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [handleCloseModal]);

  return (
    <>
      <div 
        ref={backdropRef}
        className="modal-bottom-fixed"
        onClick={handleBackdropClick}
        onTouchEnd={handleBackdropClick}
      >
        <div 
          ref={modalRef}
          className="modal-content"
          onClick={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-center p-4 border-b border-gray-200 flex-shrink-0">
            {typeof headingText === 'string' ? (
              <CommonText size="lg" className="text-[#f05a24] font-bold">
                {headingText}
              </CommonText>
            ) : (
              <div className="flex items-center gap-2">
                {headingText}
              </div>
            )}
            {!notShowCrossBtn && (
              showApplyButton ? (
                <button
                  onClick={handleCloseButtonClick}
                  className="bg-[#f05a24] text-white px-4 py-2 rounded-full font-medium focus:outline-none cursor-pointer hover:bg-[#e04a1a] transition-colors"
                  aria-label="Apply"
                >
                  {applyButtonText}
                </button>
              ) : (
                <button
                  onClick={handleCloseButtonClick}
                  className="bg-gray-100 rounded-full w-8 h-8 flex items-center justify-center focus:outline-none cursor-pointer"
                  aria-label="Close"
                >
                  <CommonText
                    size="xl"
                    className="font-bold mt-[-4px] text-gray-500"
                  >
                    &times;
                  </CommonText>
                </button>
              )
            )}
          </div>
          {children}
        </div>
      </div>
    </>
  );
};

export default ModalLayoutBottomFixed;
